import type { AxiosError } from 'axios';
import {
  CAPTIVE_PORTAL,
  TIM_ATIVACAO_CHIP,
  APP_CHANNELS,
} from '../constants/channels';
import type { Channel } from '../types/channel';
import { buildGa4Event } from '../lib/tracking/utils';

// Enum das mensagens = ['m4u_sucesso', 'm4u_processamento', 'm4u_erro']
export function sendPostMessageCaptivePortal(
  message: string,
  channelName: Channel,
): void {
  if (channelName === CAPTIVE_PORTAL || channelName === TIM_ATIVACAO_CHIP) {
    window.parent.postMessage(message, '*');
  }
}

const mountPostMessageLoginData = ({
  authCode = null,
  err = null,
}: {
  authCode?: string | null;
  err?: AxiosError | null;
}) => {
  // Não mudar a estrutura. Foi um pedido do cliente.
  const eventStatus = err ? 'falha' : 'sucesso';
  const eventStatusApp = err ? 'NOK' : 'OK';

  const httpStatus = err?.response?.status ?? 0;
  const messageErrorDetail = `Message: ${
    err?.response?.data?.message ? err.response.data.message : 'Error'
  }`;
  const eventError = eventStatus === 'sucesso' ? '' : String(httpStatus);
  const eventErrorDetail = eventStatus === 'sucesso' ? '' : messageErrorDetail;
  const error =
    eventStatusApp === 'OK'
      ? {}
      : { name: 'session-error', detail: eventErrorDetail };

  return {
    action: 'authenticate',
    event: 'authenticate',
    eventStatus: eventStatusApp,
    value: authCode,
    error,
    // O evento GA4 de autenticação vai nos dois caminhos (event-status
    // sucesso|falha). Não duplica o view da tela de recarga
    // (trackAnalyticsRechargePageview) porque o event-label aqui é
    // 'authenticate', e não 'selecionar_valor_recarga'.
    ...buildGa4Event({
      type: 'view',
      location: 'recarga_bemobi',
      label: 'authenticate',
      status: eventStatus,
      error: eventError,
      errorDetail: eventErrorDetail,
    }),
  };
};

const sendPostMessageTim = (
  data: Record<string, unknown>,
  channel: Channel,
  { stringify = true }: { stringify?: boolean } = {},
): void => {
  const isAppChannel = (APP_CHANNELS as readonly string[]).includes(channel);
  const dataString = JSON.stringify(data);

  // Canal do App — Flutter sempre recebe string
  if (isAppChannel && window?.flutter_inappwebview !== undefined) {
    window.flutter_inappwebview.callHandler('FlutterChannel', dataString);
  }

  // Canal da Web
  if (!isAppChannel && window?.parent !== undefined) {
    window.parent.postMessage(stringify ? dataString : data, '*');
  }
};

export const sendGtmPostMessageTim = (
  data: Record<string, unknown>,
  channel: Channel,
): void => sendPostMessageTim(data, channel, { stringify: false });

export const sendGa4PostMessageTim = (
  data: Record<string, unknown>,
  channel: Channel,
): void => sendPostMessageTim(data, channel, { stringify: true });

// O evento 'authenticate' só existe no fluxo de auth_code (/api-payment). O
// login por token 't' compartilha o mesmo verifyQueryStringToken, mas não é
// esperado pela operadora — sem authCode, não emite.
export function sendCreateSessionSuccess({
  authCode,
  channel,
}: {
  authCode: string;
  channel: Channel;
}): void {
  if (!authCode) return;
  const data = mountPostMessageLoginData({ authCode });
  sendPostMessageTim(data, channel);
}

// Avisa o parent (App/Web) do estado de decisão do upsell de controle, pra ele
// persistir e não reenviar upsellPlan como elegível numa próxima sessão.
export function sendUpsellPlanState({
  upsellPlan,
  channel,
}: {
  upsellPlan: boolean;
  channel: Channel;
}): void {
  sendPostMessageTim({ upsellPlan }, channel);
}

export function sendCreateSessionError({
  authCode,
  err,
  channel,
}: {
  authCode: string;
  err: AxiosError;
  channel: Channel;
}): void {
  if (!authCode) return;
  const data = mountPostMessageLoginData({ authCode, err });
  sendPostMessageTim(data, channel);
}
